import React, { useState } from 'react';
import './ChatSidebar.css';

const ChatSidebar = ({ 
  conversations, 
  activeConversationId, 
  onNewConversation, 
  onSelectConversation,
  onDeleteConversation,
  collapsed 
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Format timestamp to relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Group conversations by date
  const groupConversations = (conversations) => {
    const today = [];
    const yesterday = [];
    const lastWeek = [];
    const older = [];

    const now = new Date();
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const lastWeekStart = new Date(todayStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    conversations.forEach(conv => {
      const convDate = new Date(conv.timestamp);
      
      if (convDate >= todayStart) {
        today.push(conv);
      } else if (convDate >= yesterdayStart) {
        yesterday.push(conv);
      } else if (convDate >= lastWeekStart) {
        lastWeek.push(conv);
      } else {
        older.push(conv);
      }
    });

    return { today, yesterday, lastWeek, older };
  };

  // Filter conversations by search term
  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const grouped = groupConversations(filteredConversations);

  const renderConversationGroup = (title, convs) => {
    if (convs.length === 0) return null;

    return (
      <div className="conversation-group">
        <div className="group-title">{title}</div>
        {convs.map(conv => (
          <div
            key={conv.id}
            className={`conversation-item ${conv.id === activeConversationId ? 'active' : ''}`}
            onClick={() => onSelectConversation(conv.id)}
          >
            <div className="conversation-content">
              <div className="conversation-title">{conv.title}</div>
              <div className="conversation-meta">
                {conv.messages.length} msgs • {formatRelativeTime(conv.timestamp)}
              </div>
            </div>
            <button
              className="delete-conversation-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Delete this conversation?')) {
                  onDeleteConversation(conv.id);
                }
              }}
              title="Delete conversation"
            >
              🗑️
            </button>
          </div>
        ))}
      </div>
    );
  };

  if (collapsed) {
    return (
      <div className="chat-sidebar collapsed">
        <button 
          className="new-chat-btn-collapsed"
          onClick={onNewConversation}
          title="New Conversation"
        >
          +
        </button>
      </div>
    );
  }

  return (
    <div className="chat-sidebar">
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <button className="new-chat-btn" onClick={onNewConversation}>
          <span className="btn-icon">+</span>
          New Conversation
        </button>
      </div>

      {/* Search Bar */}
      <div className="sidebar-search">
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="clear-search-btn"
            onClick={() => setSearchTerm('')}
          >
            ✕
          </button>
        )}
      </div>

      {/* Conversations List */}
      <div className="conversations-list">
        {filteredConversations.length === 0 ? (
          <div className="empty-conversations">
            <p>No conversations found</p>
          </div>
        ) : (
          <>
            {renderConversationGroup('Today', grouped.today)}
            {renderConversationGroup('Yesterday', grouped.yesterday)}
            {renderConversationGroup('Last 7 days', grouped.lastWeek)}
            {renderConversationGroup('Older', grouped.older)}
          </>
        )}
      </div>

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        <div className="footer-stats">
          <span>{conversations.length} conversations</span>
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
